//
// # Text Annotation Context Menu
//
// Displays actions when right-clicking on a text annotation in a schematic.
// Text editing is handled via a modal dialog in the IDE (consistent with other
// edit dialogs like Edit Wire Label, Rename Pin, etc.).
//

import type { TextEntity } from "DrawCore/src/primitives/text";
import { ContentKind } from "DrawCore/src/content/kind";
import { Annotation } from "DrawCore/src/content/shared";
import { Palette } from "DrawCore/src/style";
import {
  BaseContextMenu,
  escapeHtml,
  type ContextMenuStyleConfig,
} from "./baseContextMenu";

export class TextAnnotationContextMenu extends BaseContextMenu {
  // Note: Explicit constructor omitted per lint rule (@typescript-eslint/no-useless-constructor).
  // The base class constructor handles editor initialization.

  // The text annotation under the mouse pointer
  private currentText: TextEntity | null = null;

  // -- Style config --

  readonly styleConfig: ContextMenuStyleConfig = {
    minWidth: "150px",
    maxWidth: "300px",
  };

  // -- Subclass stylesheet --

  protected getSubclassStyleSheet(): string {
    const p = Palette.getAll();
    return `
      .context-menu-header {
        font-weight: bold;
        margin-bottom: 4px;
        color: ${p.textTarget};
      }
      .context-menu-detail {
        color: ${p.textMuted};
        font-size: 11px;
      }
    `;
  }

  // -- clearState --

  protected clearState(): void {
    this.currentText = null;
  }

  // -- Show --

  // Show context menu for a text annotation at the given position.
  show = (text: TextEntity, pageX: number, pageY: number): void => {
    // Close any other open context menus first
    this.editor.hideAllContextMenus();

    this.currentText = text;

    // Create or update menu element
    this.ensureMenuElement();

    // Build menu content
    const content = this.buildMenuContent(text);

    this.displayMenu(content, pageX, pageY);

    // Set up click handlers for menu items
    this.setupMenuItemHandlers();
  };

  // Build the HTML content for the menu
  private buildMenuContent = (text: TextEntity): string => {
    const preview = text.data.text || "(empty)";

    let content = `<div class="context-menu-info">`;
    content += `<div class="context-menu-header">Text</div>`;
    content += `<div class="context-menu-detail">${escapeHtml(preview)}</div>`;
    content += `</div>`;

    // Add separator
    content += `<div class="context-menu-separator"></div>`;

    // Add action items
    content += `<div class="context-menu-actions">`;
    content += `<div class="context-menu-item" data-action="edit-text">Edit Text...</div>`;
    content += `<div class="context-menu-item" data-action="delete">Delete Text</div>`;
    content += `</div>`;

    return content;
  };

  // Set up click handlers for menu items
  private setupMenuItemHandlers = (): void => {
    if (!this.menuElement) return;

    const menuItems = this.menuElement.querySelectorAll(".context-menu-item");
    menuItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      item.onclick = (e) => {
        e.stopPropagation();
        const action = item.getAttribute("data-action");

        if (action === "edit-text") {
          this.handleEditText();
          this.hide();
        } else if (action === "delete") {
          this.handleDelete();
          this.hide();
        }
      };
    });
  };

  // Open the Edit Text dialog for the given text annotation.
  // Can be called directly (e.g., from a click gesture) without showing the menu.
  openEditDialog = (text: TextEntity): void => {
    const { editor } = this;
    if (editor.isLocked) return;

    // The annotation must have an ID to be editable.
    const elementId = text.data.id;
    if (elementId === undefined) return;

    // Notify the platform to open the edit dialog.
    editor.platform.notifyEditTextAnnotation(elementId, text.data.text);
  };

  // Handle edit text action from context menu
  private handleEditText = (): void => {
    const { currentText } = this;
    if (!currentText) return;
    this.openEditDialog(currentText);
  };

  // Handle delete action.
  // Deletes without confirmation, consistent with other entity deletions in the UI.
  private handleDelete = (): void => {
    const { editor, currentText } = this;
    if (!currentText || editor.isLocked) return;

    if (editor.content.contentKind === ContentKind.Schematic) {
      // Select the annotation before deleting to ensure we delete the right one.
      editor.content.select(new Annotation(currentText));
      editor.content.deleteSelected();
    }
  };
}
